import { apiClient } from './apiClient';
import { authService } from './authService';

export const chatbotService = {
  // Chatbot CRUD
  async getChatbots() {
    return apiClient.get(`/api/chatbots`);
  },

  async getChatbot(id) {
    return apiClient.get(`/api/chatbots/${id}`);
  },

  async createChatbot(data) {
    return apiClient.post(`/api/chatbots`, data);
  },

  async updateChatbot(id, data) {
    return apiClient.put(`/api/chatbots/${id}`, data);
  },

  async deleteChatbot(id) {
    return apiClient.delete(`/api/chatbots/${id}`);
  },

  async generateApiKey(chatbotId) {
    return apiClient.post(`/api/chatbots/${chatbotId}/api-key`);
  },

  async revokeApiKey(chatbotId) {
    return apiClient.delete(`/api/chatbots/${chatbotId}/api-key`);
  },

  async uploadAvatar(chatbotId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload(`/api/chatbots/${chatbotId}/avatar`, formData);
  },

  // Documents
  async getDocuments(chatbotId) {
    return apiClient.get(`/api/documents/chatbot/${chatbotId}`);
  },

  async uploadDocument(chatbotId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.upload(`/api/documents/upload?chatbotId=${chatbotId}`, formData);
  },

  async deleteDocument(documentId) {
    return apiClient.delete(`/api/documents/${documentId}`);
  },

  async reprocessDocument(documentId) {
    return apiClient.post(`/api/documents/${documentId}/reprocess`);
  },

  async addUrlSource(chatbotId, url) {
    return apiClient.post(`/api/documents/url?chatbotId=${chatbotId}&url=${encodeURIComponent(url)}`);
  },

  async getDocumentQuota(chatbotId) {
    return apiClient.get(`/api/documents/chatbot/${chatbotId}/quota`);
  },

  // Conversations
  async getConversations(chatbotId, page = 0, size = 20) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('size', size);
    if (chatbotId) params.append('chatbotId', chatbotId);
    return apiClient.get(`/api/chatbots/conversations?${params.toString()}`);
  },

  async getConversationMessages(conversationId) {
    return apiClient.get(`/api/chatbots/conversations/${conversationId}/messages`);
  },

  // Dashboard stats
  async getDashboardStats() {
    return apiClient.get(`/api/chatbots/stats`);
  },

  // Analytics (period: '7days' | '30days' | '90days')
  async getAnalytics(period = '30days') {
    return apiClient.get(`/api/chatbots/analytics?period=${period}`);
  },

  // Agent management
  async getAgents() {
    return apiClient.get(`/api/chatbot/agents`);
  },

  async createAgent(data) {
    return apiClient.post(`/api/chatbot/agents`, data);
  },

  async updateAgent(agentId, data) {
    return apiClient.put(`/api/chatbot/agents/${agentId}`, data);
  },

  async deactivateAgent(agentId) {
    return apiClient.delete(`/api/chatbot/agents/${agentId}`);
  },

  async activateAgent(agentId) {
    return apiClient.put(`/api/chatbot/agents/${agentId}/activate`);
  },

  // Agent seat usage from subscription service
  async getAgentUsage() {
    const companyId = authService.getCompanyId();
    return apiClient.get(`/api/subscriptions/chatbot/company/${companyId}/agent-usage`);
  },

  async purchaseAgentSlots(quantity) {
    const companyId = authService.getCompanyId();
    return apiClient.post(`/api/subscriptions/chatbot/${companyId}/extra-agents?quantity=${quantity}`);
  },
};

export const chatbotAdminService = {
  // companyId is accepted for caller compatibility but not sent: the backend derives the company from the token.
  getCompanyHandledConversations(companyId, page = 0, size = 20, agentId = null, status = null, search = null) {
    const params = new URLSearchParams({ page, size });
    if (agentId) params.append('agentId', agentId);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return apiClient.get(`/api/chatbot/agents/analytics/conversations?${params.toString()}`);
  },

  // companyId is accepted for caller compatibility but not sent: the backend derives the company from the token.
  getCompanySummary(companyId) {
    return apiClient.get(`/api/chatbot/agents/analytics/summary`);
  },

  // companyId is accepted for caller compatibility but not sent: the backend derives the company from the token.
  getAgentsList(companyId) {
    return apiClient.get(`/api/chatbot/agents`);
  },

  getConversationMessages(sessionId) {
    return apiClient.get(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/messages`);
  },

  addCallbackUpdate(sessionId, note) {
    const u = authService.getCurrentUser();
    const authorName = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || null : null;
    return apiClient.post(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/callback-update`, { note, authorName });
  },

  closeCallbackConversation(sessionId, note, status) {
    const u = authService.getCurrentUser();
    const authorName = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || null : null;
    return apiClient.post(`/api/chatbot/agents/analytics/conversations/${encodeURIComponent(sessionId)}/close-callback`, { note, status, authorName });
  },
};
